import React, {createElement, forwardRef} from 'react';

import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import {FlatButtonIcon} from './flat-button-icon';

import styles from './flat-button.component.module.styl';

import type {ComponentProps, ReactNode} from 'react';


const classNames = withCssModule(styles);
const classes = hashedClasses(styles);
const noop = () => undefined;

type NativeButtonProps = Omit<ComponentProps<'button'>, 'ref'>;

export interface FlatButtonProps extends NativeButtonProps {
    /**
     * Element type
     */
    as?: 'button' | 'a',
    /**
     * Set component to busy state
     */
    busy?: boolean,
    /**
     * Apply dark mode styles
     * @deprecated It will be removed in 6 months 03-03-2026. Use default appearance
     */
    darkMode?: boolean,
    /**
     * Specifies the URL the hyperlink points to. Use with as='a'
     * Temporary solution until Flow is removed
     */
    href?: string,
    /**
     * Ref for button
     * */
    ref?: React.Ref<HTMLButtonElement>,
    /**
     * Icon before caption
     */
    startIcon?: ReactNode,
    /**
     * Link target. Use with as='a'
     * Temporary solution until Flow is removed
     */
    target?: ComponentProps<'a'>['target'],
    /**
     * Icon after caption
     */
    endIcon?: ReactNode,
    /**
     * Variant of button style
     */
    variant?: 'primary' | 'secondary',
}

/**
 * @description Flat button component with primary and secondary variants and busy state
 *
 * ##Component usage notes
 * For bold text, use composition with the `<strong>` tag
 *
 * ##Features
 * - Renders as `<button>` or `<a>` element
 * - Primary and secondary variants (primary by default)
 * - Busy state with loading spinner
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/buttons-button-flat-button--documentation}
 */

export const FlatButton = forwardRef<HTMLAnchorElement | HTMLButtonElement, FlatButtonProps>(
    (props, ref) => {
        const {
            as = 'button',
            type = as === 'button' ? 'button' : undefined,
            busy,
            disabled,
            children,
            className,
            darkMode,
            endIcon,
            startIcon,
            variant = 'primary',
            ...restProps
        } = props;

        const iconProps = {flat: true, busy, darkMode};

        const content = (
            <div className={classes['flat-button__content']}>
                <FlatButtonIcon {...iconProps} iconClassName={classes['flat-button__icon']}>
                    {startIcon}
                </FlatButtonIcon>
                <span className={classes['flat-button__caption']}>{children}</span>
                <FlatButtonIcon
                    {...iconProps}
                    iconClassName={classNames('flat-button__icon', 'flat-button__icon_right', 'pdx-xxs')}
                    right={true}>
                    {endIcon}
                </FlatButtonIcon>
            </div>
        );

        const elementClassName = classNames(
            'flat-button',
            className,
            {
                'flat-button_dark-mode': darkMode,
                'flat-button_secondary': variant === 'secondary',
                'flat-button_busy': busy,
            },
        );

        const isDisabled = disabled || busy;

        return createElement(as, {
            'aria-label': busy ? 'Loading' : undefined,
            type,
            ...restProps,
            'aria-disabled': isDisabled,
            'disabled': isDisabled,
            'onClick': isDisabled ? noop : restProps.onClick,
            'className': elementClassName,
            ref,
        }, content);
    },
);

FlatButton.displayName = 'FlatButton';
